/**
 * Client portal: proposals to review and accept (which signs the engagement letter), saved payment
 * methods and automatic payment, and the page that stops review requests.
 */
import { MODULE_LABELS, PROPOSAL_BILLING_LABELS, formatCents, type Module, type ProposalBillingKind } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, CreditCard, FileSignature, FileText, Landmark } from "lucide-react";
import { useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { ask, askText } from "@/components/dialogs";
import { Empty, Loading, PageHeader, Section } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { TableScroll } from "@/components/table-scroll";

export type PortalProposal = { id: string; number: number; kind: "new" | "renewal"; status: "sent" | "accepted" | "declined" | "expired"; title: string; clientId: string; startsOn: string; termMonths: number; sentAt: string | null; validUntil: string | null; acceptedAt: string | null };

const price = (cents: number, billing: ProposalBillingKind) => `${formatCents(cents)}${billing === "one_time" ? "" : billing === "monthly" ? " a month" : billing === "quarterly" ? " a quarter" : " a year"}`;

/** Proposals waiting for the client, for the Overview's to-do list. */
export function useProposalsToAccept() {
  const { data } = useQuery<PortalProposal[]>({ queryKey: ["/api/portal/proposals"] });
  return (data ?? []).filter((p) => p.status === "sent");
}

export function ProposalTodoCards({ className }: { className?: string }) {
  const waiting = useProposalsToAccept();
  if (!waiting.length) return null;
  return (
    <ul className={cn("mb-3 space-y-3", className)}>
      {waiting.map((p) => (
        <li key={p.id}>
          <Link href={`/proposals/${p.id}`} className="flex items-center gap-3 rounded-xl border border-card-border bg-card p-4 shadow-xs hover:border-primary/50">
            <FileSignature className="h-5 w-5 text-primary" />
            <span className="flex-1">
              <span className="block font-medium">{p.kind === "renewal" ? "Renewal to review" : "Proposal to review"}: {p.title}</span>
              <span className="text-sm text-muted-foreground">{p.validUntil ? `Please review and accept it by ${formatDate(p.validUntil)}.` : "Please review and accept it."}</span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

const STATUS_TEXT: Record<PortalProposal["status"], { label: string; tone: string }> = {
  sent: { label: "Waiting for you", tone: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200" },
  accepted: { label: "Accepted", tone: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200" },
  declined: { label: "Declined", tone: "bg-secondary text-muted-foreground" },
  expired: { label: "Expired", tone: "bg-secondary text-muted-foreground" },
};

/** On Invoices & Payments: the proposals and engagement letters the firm has sent. */
export function PortalProposalsSection() {
  const { data } = useQuery<PortalProposal[]>({ queryKey: ["/api/portal/proposals"] });
  if (!data?.length) return null;
  return (
    <Section title="Proposals and engagement letters" description="The services and fees your firm has proposed. Accepting a proposal signs its engagement letter.">
      <ul className="divide-y divide-border">
        {data.map((p) => (
          <li key={p.id}>
            <Link href={`/proposals/${p.id}`} className="flex items-center gap-3 py-2.5 text-sm hover:text-primary">
              <span className="min-w-0 flex-1 truncate font-medium">{p.title}</span>
              <span className="tabular hidden whitespace-nowrap text-xs text-muted-foreground sm:inline">{p.sentAt ? formatDate(p.sentAt) : ""}</span>
              <span className={cn("whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium", STATUS_TEXT[p.status].tone)}>{STATUS_TEXT[p.status].label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Section>
  );
}

type Detail = {
  proposal: {
    id: string;
    number: number;
    kind: "new" | "renewal";
    status: PortalProposal["status"];
    title: string;
    message: string;
    startsOn: string;
    endsOn: string;
    termMonths: number;
    validUntil: string | null;
    acceptedAt: string | null;
    acceptedName: string | null;
    letterDocumentId: string | null;
    signedDocumentId: string | null;
    totals: { oneTimeCents: number; annualCents: number; firstYearCents: number };
  };
  items: Array<{ id: string; module: Module; description: string; billing: ProposalBillingKind; amountCents: number; period: string | null }>;
  firmName: string;
  signerName: string;
};

export function PortalProposalPage({ id }: { id: string }) {
  const [, navigate] = useLocation();
  const { data, isLoading, error } = useQuery<Detail>({ queryKey: [`/api/portal/proposals/${id}`] });
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const accept = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("POST", `/api/portal/proposals/${id}/accept`, { name, agree: true }) });
  const decline = useMutation({ mutationFn: (note: string) => api.send("POST", `/api/portal/proposals/${id}/decline`, { note }), onSuccess: () => navigate("/invoices") });
  if (isLoading) return <Loading />;
  if (error || !data)
    return (
      <>
        <h1 className="sr-only">Proposal</h1>
        <Empty>{errorMessage(error)}</Empty>
      </>
    );
  const p = data.proposal;
  const letter = (docId: string | null, label: string) =>
    docId && (
      <a href={`/api/portal/documents/${docId}/download`} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
        <FileText className="h-4 w-4" aria-hidden /> {label}
      </a>
    );
  return (
    <>
      <PageHeader
        back={{ label: "Invoices & Payments", href: "/invoices" }}
        title={p.title}
        description={`${p.kind === "renewal" ? "Renewal proposal" : "Proposal"} from ${data.firmName} for ${p.termMonths} months, from ${formatDate(p.startsOn)} to ${formatDate(p.endsOn)}.`}
        actions={<span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", STATUS_TEXT[p.status].tone)}>{STATUS_TEXT[p.status].label}</span>}
      />
      <div className="space-y-6">
        {p.message && (
          <Section title={`A note from ${data.firmName}`}>
            <p className="whitespace-pre-line text-sm">{p.message}</p>
          </Section>
        )}
        <Section title="Services and fees">
          <TableScroll label="Services and fees table">
            <table className="w-full min-w-[480px] text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3 font-medium">Service</th>
                  <th className="px-3 py-2 font-medium">Billed</th>
                  <th className="py-2 pl-3 text-right font-medium">Fee</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.items.map((i) => (
                  <tr key={i.id}>
                    <td className="py-2.5 pr-3">
                      {i.description}
                      <span className="block text-xs text-muted-foreground">{MODULE_LABELS[i.module]}</span>
                    </td>
                    <td className="px-3 py-2.5">{PROPOSAL_BILLING_LABELS[i.billing]}</td>
                    <td className="tabular py-2.5 pl-3 text-right font-medium">{price(i.amountCents, i.billing)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
          <dl className="mt-4 grid gap-3 border-t border-border pt-4 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs text-muted-foreground">One-time fees</dt>
              <dd className="tabular text-lg font-semibold">{formatCents(p.totals.oneTimeCents)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Recurring fees for a year</dt>
              <dd className="tabular text-lg font-semibold">{formatCents(p.totals.annualCents)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">First year in total</dt>
              <dd className="tabular text-lg font-semibold">{formatCents(p.totals.firstYearCents)}</dd>
            </div>
          </dl>
        </Section>
        <Section title="Engagement letter" description="The terms of the engagement: scope, responsibilities, fees, term and renewal. Please read it before accepting.">
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {letter(p.letterDocumentId, "Read the engagement letter (PDF)")}
            {letter(p.signedDocumentId, "Your signed copy (PDF)")}
          </div>
        </Section>
        {p.status === "sent" && (
          <Section title="Accept and sign" description={p.validUntil ? `This proposal can be accepted until ${formatDate(p.validUntil)}.` : undefined}>
            <div className="space-y-4">
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-0.5" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                <span>I have read the engagement letter. I agree to the services, fees and terms, and to sign electronically. My typed name is my signature and has the same effect as a handwritten one.</span>
              </label>
              <div className="max-w-sm space-y-1.5">
                <Label htmlFor="sign-name">Type your full name to sign</Label>
                <Input id="sign-name" autoComplete="name" placeholder={data.signerName} value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              {accept.error && <p className="text-sm text-destructive">{errorMessage(accept.error)}</p>}
              <div className="flex flex-wrap gap-2">
                <Button disabled={!agree || !name.trim() || accept.isPending} onClick={() => accept.mutate()}>
                  Accept and sign
                </Button>
                <Button
                  variant="ghost"
                  onClick={async () => {
                    const note = await askText({ title: "Decline this proposal?", description: "Your firm will be told.", label: "A note for your firm (optional)", confirmLabel: "Decline proposal", destructive: true });
                    if (note !== null) decline.mutate(note);
                  }}
                >
                  Decline
                </Button>
              </div>
            </div>
          </Section>
        )}
        {p.status === "accepted" && (
          <p className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
            <CheckCircle2 className="h-4 w-4" /> Accepted and signed by {p.acceptedName} on {formatDate(p.acceptedAt)}. Thank you.
          </p>
        )}
      </div>
    </>
  );
}

// --- Saved payment methods and automatic payment -----------------------------------------------

type Methods = {
  available: boolean;
  methods: Array<{ id: string; kind: "card" | "us_bank_account" | "other"; label: string; autopay: boolean; expMonth: number | null; expYear: number | null }>;
  schedules: Array<{ id: string; description: string; amountCents: number; interval: "month" | "quarter" | "year"; nextOn: string; status: string }>;
  consentText: string;
  signerName: string;
};

export function PortalPaymentMethods({ clientId, clientName }: { clientId: string; clientName?: string }) {
  const { data } = useQuery<Methods>({ queryKey: [`/api/portal/payment-methods?clientId=${clientId}`] });
  const search = new URLSearchParams(useSearch());
  const [choosing, setChoosing] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const setup = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ url: string }>("POST", "/api/portal/payment-methods/setup", { clientId }),
    onSuccess: (r) => {
      window.location.href = r.url;
    },
  });
  const on = useMutation({ meta: { inlineError: true }, mutationFn: (methodId: string) => api.send("POST", "/api/portal/payment-methods/autopay", { methodId, name, agree: true }), onSuccess: () => setChoosing(null) });
  const off = useMutation({ mutationFn: () => api.send("DELETE", `/api/portal/payment-methods/autopay?clientId=${clientId}`) });
  const remove = useMutation({ mutationFn: (id: string) => api.send("DELETE", `/api/portal/payment-methods/${id}`) });
  if (!data || !data.available) return null;
  const autopay = data.methods.find((m) => m.autopay);
  const per = { month: "a month", quarter: "a quarter", year: "a year" } as const;
  return (
    <Section
      title={clientName ? `Automatic payment for ${clientName}` : "Automatic payment"}
      description="Save a card or bank account to pay invoices automatically on the day they are issued. Your card or bank details are kept by Stripe, our payment processor, not by your firm."
      action={
        <Button size="sm" variant="outline" disabled={setup.isPending} onClick={() => setup.mutate()}>
          Add a card or bank account
        </Button>
      }
    >
      {search.get("saved") === "1" && !data.methods.length && <p className="mb-3 rounded-md bg-muted/60 px-3 py-2 text-sm">Thank you. Your payment method will appear here in a moment. A bank account can take a few days to verify.</p>}
      {setup.error && <p className="mb-3 text-sm text-destructive">{errorMessage(setup.error)}</p>}
      {data.schedules.length > 0 && (
        <p className="mb-3 text-sm text-muted-foreground">
          Recurring fees: {data.schedules.map((s) => `${s.description} (${formatCents(s.amountCents)} ${per[s.interval]}, next on ${formatDate(s.nextOn)})`).join("; ")}.
        </p>
      )}
      {!data.methods.length ? (
        <Empty>No saved payment method. Choose Add a card or bank account to save one on Stripe's secure page.</Empty>
      ) : (
        <ul className="divide-y divide-border">
          {data.methods.map((m) => (
            <li key={m.id} className="py-3">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                {m.kind === "us_bank_account" ? <Landmark className="h-4 w-4 text-muted-foreground" /> : <CreditCard className="h-4 w-4 text-muted-foreground" />}
                <span className="min-w-0 flex-1 truncate font-medium">
                  {m.label}
                  {m.expMonth && m.expYear && <span className="ml-2 text-xs font-normal text-muted-foreground">Expires {String(m.expMonth).padStart(2, "0")}/{m.expYear}</span>}
                </span>
                {m.autopay ? (
                  <>
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200">Pays automatically</span>
                    <Button size="sm" variant="ghost" onClick={() => off.mutate()}>
                      Turn off
                    </Button>
                  </>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setChoosing(m.id)}>
                    Pay automatically with this
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => (await ask({ title: `Remove ${m.label}?`, description: m.autopay ? "Automatic payment will stop. Invoices will be emailed for you to pay." : undefined, confirmLabel: "Remove", destructive: true })) && remove.mutate(m.id)}
                >
                  Remove
                </Button>
              </div>
              {choosing === m.id && (
                <div className="mt-3 space-y-3 rounded-lg bg-muted/40 p-3 text-sm">
                  <label className="flex items-start gap-2">
                    <input type="checkbox" className="mt-0.5" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                    <span>{data.consentText}</span>
                  </label>
                  <div className="max-w-sm space-y-1.5">
                    <Label htmlFor={`ap-${m.id}`}>Type your full name to authorize</Label>
                    <Input id={`ap-${m.id}`} autoComplete="name" placeholder={data.signerName} value={name} onChange={(e) => setName(e.target.value)} />
                  </div>
                  {on.error && <p className="text-destructive">{errorMessage(on.error)}</p>}
                  <div className="flex gap-2">
                    <Button size="sm" disabled={!agree || !name.trim() || on.isPending} onClick={() => on.mutate(m.id)}>
                      Turn on automatic payment
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setChoosing(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {!autopay && data.methods.length > 0 && <p className="mt-2 text-xs text-muted-foreground">Automatic payment is off. Invoices are emailed for you to pay by card, bank account, or any other way your firm accepts.</p>}
    </Section>
  );
}

// --- Review request preferences (no sign-in) -------------------------------------------------

export function ReviewPreferencesPage() {
  const ref = new URLSearchParams(window.location.hash.slice(1)).get("ref") ?? "";
  const stop = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send<{ firm: string }>("POST", "/api/public/reviews/opt-out", { ref }) });
  return (
    <main className="mx-auto max-w-lg px-4 py-16">
      <h1 className="mb-2 text-xl font-semibold">Review requests</h1>
      {stop.isSuccess ? (
        <p className="text-sm">{stop.data.firm} will not send you review requests again.</p>
      ) : (
        <>
          <p className="mb-4 text-sm text-muted-foreground">Stop receiving emails that ask for a review of your firm. Other emails from your firm, such as invoices and requests, are not affected.</p>
          {stop.error && <p className="mb-3 text-sm text-destructive">{errorMessage(stop.error)}</p>}
          <Button disabled={!ref || stop.isPending} onClick={() => stop.mutate()}>
            Stop review requests
          </Button>
        </>
      )}
    </main>
  );
}
